import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Switch,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useAuth } from "../../authContext/authContext";
import { updateMyProfile } from "../../Services/apiService";
import { BRAND_BLUE } from "../../theme/appTheme";
import { useTranslation } from "../../i18n/LanguageContext";
import { useUnsavedChanges } from "./unsavedChanges";

// users.two_factor_enabled is NULL until the user chooses — the backend then
// still sends the login code to patients/therapists but not to
// admins/operators (routes/Auth.js requiresLoginOtp), so show the switch in
// whichever position actually matches today's login behaviour.
function effectiveValue(raw, roles) {
  if (raw === true || raw === false) return raw;
  return !roles.some((r) => r === "admin" || r === "operator");
}

export default function TwoFactorSettings({ loading, initialValue, loadFailed, actionButtonSx, secondaryButtonSx }) {
  const { t } = useTranslation();
  const { roles, updateUserFields } = useAuth();

  const [saved, setSaved] = useState(initialValue);
  const [enabled, setEnabled] = useState(effectiveValue(initialValue, roles));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setSaved(initialValue);
    setEnabled(effectiveValue(initialValue, roles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  // Compared against what the switch showed on load (NULL resolves to the
  // effective behaviour), so Save only appears once the user flips it.
  const dirty = enabled !== effectiveValue(saved, roles);

  // Shared by the Save -> confirm path and by SettingsDialog's
  // unsaved-changes "Save" (where that prompt is the confirmation).
  const persist = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateMyProfile({ twoFactorEnabled: enabled });
      setSaved(enabled);
      updateUserFields({ twoFactorEnabled: enabled });
      setSuccess(t("settings.account.twoFactor.saved"));
      return true;
    } catch (err) {
      setError(err.message || t("settings.account.twoFactor.saveError"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    await persist();
    setConfirmOpen(false);
  };

  useUnsavedChanges("twoFactor", dirty, persist);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827", mb: 2 }}>
        {t("settings.account.twoFactor.title")}
      </Typography>

      {loading ? (
        <CircularProgress size={18} sx={{ color: "#9ca3af" }} />
      ) : loadFailed ? (
        <Alert severity="error" sx={{ fontSize: "0.8125rem", py: 0 }}>
          {t("settings.account.twoFactor.loadError")}
        </Alert>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 3 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.8125rem", color: "#111827" }}>
                {t("settings.account.twoFactor.label")}
              </Typography>
              <Typography sx={{ fontSize: "0.71875rem", color: "#6b7280", mt: 0.25, lineHeight: 1.4 }}>
                {enabled ? t("settings.account.twoFactor.noteOn") : t("settings.account.twoFactor.noteOff")}
              </Typography>
            </Box>
            <Switch
              checked={enabled}
              onChange={(e) => {
                setEnabled(e.target.checked);
                setSuccess("");
                setError("");
              }}
              inputProps={{ "aria-label": t("settings.account.twoFactor.label") }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_BLUE },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND_BLUE },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, "&:empty": { display: "none" } }}>
            {/* Only offered once the switch differs from what's saved. */}
            {dirty && (
              <Button
                variant="contained"
                onClick={() => setConfirmOpen(true)}
                disabled={saving}
                sx={actionButtonSx}
              >
                {t("settings.account.twoFactor.save")}
              </Button>
            )}
            {error && (
              <Alert severity="error" sx={{ fontSize: "0.8125rem", py: 0 }}>
                {error}
              </Alert>
            )}
            {success && !error && !dirty && (
              <Alert severity="success" sx={{ fontSize: "0.8125rem", py: 0 }}>
                {success}
              </Alert>
            )}
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          {enabled ? t("settings.account.twoFactor.confirmTitleOn") : t("settings.account.twoFactor.confirmTitleOff")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.8125rem", color: "#4b5563", lineHeight: 1.5 }}>
            {enabled ? t("settings.account.twoFactor.confirmBodyOn") : t("settings.account.twoFactor.confirmBodyOff")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmOpen(false)} disabled={saving} sx={secondaryButtonSx}>
            {t("settings.account.twoFactor.cancel")}
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={saving} sx={actionButtonSx}>
            {saving ? <CircularProgress size={16} sx={{ color: "#1a1a1a" }} /> : t("settings.account.twoFactor.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
